/**
 * Онбординг организации: название, отрасль, адрес (ТЗ §3, §14.1).
 *
 * Главное на экране — не форма, а живая подстановка пресета: владелец выбирает отрасль
 * и сразу видит, какие правила получит организация (шаг сетки, окно отмены, лимиты, срок
 * ограничения) и как в ней будет называться специалист. Отрасль без этого выглядит
 * выбором иконки, а на деле задаёт всю механику записи и антиспама.
 *
 * ПРЕСЕТ ЗДЕСЬ ЧИТАТЬ МОЖНО, И ТОЛЬКО ЗДЕСЬ. Организации ещё нет; при создании значения
 * КОПИРУЮТСЯ в её строку и после этого не читаются из пресета никогда (AGENTS.md,
 * правило 5) — на экране «Правила и антиспам» они берутся из `CabinetBusinessDto`.
 */

import { useMemo, useState } from 'react';
import type { BusinessType } from '@app/shared';
import { useAppApi } from '../../app/ApiProvider';
import { industryName, industryOptions } from '../../domain/catalog';
import { useI18n } from '../../i18n/I18nProvider';
import { CtaBar, CtaButton } from '../../ui/Cta';
import { LangSwitch } from '../../ui/controls';
import { ICON_STROKE, INDUSTRY_ICON } from '../../ui/icons';
import { ErrorState } from '../../ui/feedback';
import { Screen, ScrollArea, SectionTitle, TopBar } from '../../ui/Screen';
import { Card, Field, Hint, ValueRow } from './profile/fields';
import {
  onboardingBlocker,
  presetRows,
  presetValueText,
  type TranslateFn,
} from './profile/industry';
import { ADDRESS_MAX_LENGTH, NAME_MAX_LENGTH, NAME_MIN_LENGTH } from './profile/businessProfile';

export function OnboardingScreen(props: {
  onCreated: (businessId: string) => void;
  onBack: () => void;
}): JSX.Element {
  const { t, lang } = useI18n();
  const api = useAppApi();

  /**
   * Кто регистрируется: частный мастер или заведение.
   *
   * Первым вопросом и до всего остального, потому что от него зависит следующий же
   * экран: у одиночки спрашивается имя человека, а не название салона, и мастер
   * заводится сервером сам. Спросить это после названия значило бы заставить репетитора
   * сначала придумать себе вывеску.
   */
  const [isSolo, setIsSolo] = useState(false);
  const [name, setName] = useState('');
  // «Тронуто» отдельно от значения: пустое поле при открытии экрана — не ошибка владельца,
  // и красная подпись под ним до первого ввода читалась бы как претензия ни за что.
  const [nameTouched, setNameTouched] = useState(false);
  const [type, setType] = useState<BusinessType | null>(null);
  const [address, setAddress] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const options = useMemo(() => industryOptions(lang), [lang]);
  const selected = useMemo(
    () => (type === null ? null : options.find((preset) => preset.type === type) ?? null),
    [options, type],
  );

  const blocker = onboardingBlocker({ name, type, address });

  const submit = async (): Promise<void> => {
    if (type === null || blocker !== null || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      // `tz` не передаём: пояс по умолчанию задаёт бэкенд. Подставить его здесь значило бы
      // завести второе место, где живёт «Asia/Tashkent», и разъехаться с первым.
      const created = await api.cabinet.createBusiness({
        name: name.trim(),
        type,
        address: address.trim() === '' ? null : address.trim(),
        isSolo,
      });
      props.onCreated(created.id);
    } catch (failure) {
      setError(failure);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Screen>
      <TopBar title={t('onbTitle')} onBack={props.onBack} right={<LangSwitch />} />

      <ScrollArea bottomGap="cta">
        <div className="px-4 pt-5">
          <SectionTitle>{t('onbWho')}</SectionTitle>
          <div className="grid grid-cols-2 gap-2">
            {([false, true] as const).map((solo) => (
              <button
                key={String(solo)}
                type="button"
                onClick={() => setIsSolo(solo)}
                className={`rounded-card px-3 py-3 text-left transition ${
                  isSolo === solo
                    ? 'bg-surface shadow-[inset_0_0_0_2px_var(--accent)]'
                    : 'bg-surface shadow-[inset_0_0_0_1px_var(--hair)]'
                }`}
              >
                <div className="text-[13.5px] font-bold">
                  {t(solo ? 'onbWhoSolo' : 'onbWhoPlace')}
                </div>
                <div className="mt-1 text-[11.5px] leading-[1.35] text-ink-3">
                  {t(solo ? 'onbWhoSoloNote' : 'onbWhoPlaceNote')}
                </div>
              </button>
            ))}
          </div>
        </div>

        <div className="px-4 pt-5">
          <Field
            label={t(isSolo ? 'onbNameSolo' : 'onbName')}
            value={name}
            placeholder={t(isSolo ? 'onbNameSoloPh' : 'onbNamePh')}
            onChange={(value) => {
              setName(value);
              setNameTouched(true);
            }}
            error={nameErrorText(name, nameTouched, t)}
            note={t('onbSlugNote')}
          />
        </div>

        <div className="px-4 pt-1">
          <SectionTitle>{t('onbType')}</SectionTitle>
          <div className="grid grid-cols-3 gap-2">
            {options.map((preset) => {
              const Icon = INDUSTRY_ICON[preset.type];
              return (
              <button
                key={preset.type}
                type="button"
                onClick={() => setType(preset.type)}
                className={`flex flex-col items-center gap-1.5 rounded-card px-2 py-3 text-center transition ${
                  preset.type === type
                    ? 'bg-surface shadow-[inset_0_0_0_2px_var(--accent)]'
                    : 'bg-surface shadow-[inset_0_0_0_1px_var(--hair)]'
                }`}
              >
                <Icon size={24} strokeWidth={ICON_STROKE} aria-hidden="true" />
                <span className="clamp-2 text-[11.5px] font-semibold leading-[1.25]">
                  {industryName(preset, lang)}
                </span>
              </button>
              );
            })}
          </div>
          <Hint>{t('onbTypeNote')}</Hint>
        </div>

        <div className="px-4 pt-5">
          <SectionTitle>{t('onbPresetTitle')}</SectionTitle>
          {selected ? (
            <>
              <Card>
                {presetRows(selected).map((row) => (
                  <ValueRow
                    key={row.labelKey}
                    label={t(row.labelKey)}
                    value={presetValueText(row.value, t)}
                  />
                ))}
              </Card>
              {/* Ключевое обещание экрана: значения копируются и правятся вручную. Без него
                  владелец решил бы, что отрасль потом не переиграть, и выбирал бы наугад. */}
              <Hint>{t('onbPresetNote')}</Hint>
            </>
          ) : (
            <div className="rounded-card bg-surface px-3.5 py-4 text-[13px] leading-[1.5] text-ink-3 shadow-[inset_0_0_0_1px_var(--hair)]">
              {t('onbPickFirst')}
            </div>
          )}
        </div>

        <div className="px-4 pt-5">
          <Field
            label={t('onbAddress')}
            value={address}
            placeholder={t('onbAddressPh')}
            onChange={setAddress}
            error={
              blocker === 'addressLong' ? t('errAddressLong', { max: ADDRESS_MAX_LENGTH }) : undefined
            }
          />
        </div>

        {error ? <ErrorState error={error} /> : null}
      </ScrollArea>

      <CtaBar>
        <CtaButton disabled={blocker !== null || submitting} onClick={() => void submit()}>
          {/* Подпись ведёт по форме: пока отрасль не выбрана, кнопка говорит именно это,
              а не молчит серым прямоугольником «Создать». */}
          {blocker === 'noType' ? t('onbNeedType') : t('onbSubmit')}
        </CtaButton>
      </CtaBar>
    </Screen>
  );
}

/**
 * Ошибка под полем названия.
 *
 * Считается по самому названию, а не по `onboardingBlocker`: тот отдаёт ПЕРВУЮ причину,
 * почему форма не готова, и при невыбранной отрасли молчал бы про слишком длинное имя.
 * Подпись кнопки ведёт по форме, а поле отвечает за себя.
 */
function nameErrorText(
  name: string,
  touched: boolean,
  t: TranslateFn,
): string | undefined {
  const trimmed = name.trim();
  if (trimmed.length > NAME_MAX_LENGTH) return t('errNameLong', { max: NAME_MAX_LENGTH });
  if (touched && trimmed.length < NAME_MIN_LENGTH) {
    return t('errNameShort', { min: NAME_MIN_LENGTH });
  }
  return undefined;
}
